
import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";

function GetService() {
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalServices, setTotalServices] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: "",
    addedBy: "",
    vendor: "",
  });
  const [isAddedByOpen, setIsAddedByOpen] = useState(false);
  const [isVendorOpen, setIsVendorOpen] = useState(false);
  const [searchVendor, setSearchVendor] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [uniqueServiceNames, setUniqueServiceNames] = useState([]);
  const [bulkSelectName, setBulkSelectName] = useState("");
  const [newName, setNewName] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const limit = 30;

  const navigate = useNavigate();
  const addedByDropdownRef = useRef(null);
  const vendorDropdownRef = useRef(null);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        addedByDropdownRef.current &&
        !addedByDropdownRef.current.contains(event.target)
      ) {
        setIsAddedByOpen(false);
      }
      if (
        vendorDropdownRef.current &&
        !vendorDropdownRef.current.contains(event.target)
      ) {
        setIsVendorOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch services with backend pagination
  const fetchServiceData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      const queryParams = new URLSearchParams({
        page,
        limit,
      });

      const serviceRes = await fetch(`https://visitguru.in/api/v1/service/getService?${queryParams}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const serviceData = await serviceRes.json();
      if (serviceData.success) {
        setServices(serviceData.data || []);
        setTotalServices(serviceData.totalServices || 0);
        setTotalPages(serviceData.pagination?.totalPages || 1);
      } else {
        toast.error("Failed to fetch services");
      }
    } catch (error) {
      console.error("Error fetching services:", error);
      toast.error("Failed to fetch services: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories and vendors
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");

        const [categoryRes, vendorRes] = await Promise.all([
          fetch("https://visitguru.in/api/v1/category/getCategory", {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }),
          fetch("https://visitguru.in/api/v1/freelancer/getAllfreelancer", {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }),
        ]);

        const categoryData = await categoryRes.json();
        const vendorData = await vendorRes.json();

        if (categoryData.success) setCategories(categoryData.data || []);
        else toast.error("Failed to fetch categories");

        if (vendorData.success) setVendors(vendorData.data || []);
        else toast.error("Failed to fetch vendors");
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to fetch data: " + error.message);
      }
    };

    fetchData();
  }, []);

  // Fetch services when page changes
  useEffect(() => {
    fetchServiceData();
  }, [page]);

  // Compute unique service names based on current filters
  useEffect(() => {
    let filteredServices = services;

    // If category filter is applied, only show services from that category
    if (filters.category) {
      filteredServices = services.filter(s =>
        (s.category?._id || s.category) === filters.category
      );
    }

    const unique = [...new Set(filteredServices.map((s) => s.serviceName || "Unknown"))].sort();
    setUniqueServiceNames(unique);
  }, [services, filters.category]);

  // Fetch services based on selected vendor
  useEffect(() => {
    const fetchServicesByVendor = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");

        let url = "https://visit.visitguru.inhttps://visitguru.in/api/v1/service/getService";
        if (filters.vendor) {
          url += `?vendorID=${filters.vendor}`;
        }

        const serviceRes = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        const serviceData = await serviceRes.json();
        if (serviceData.success) {
          setServices(serviceData.data || []);
          const uniqueCategoryIds = [
            ...new Set(
              serviceData.data
                .map((service) => service.category?._id || service.category)
                .filter(Boolean)
            ),
          ];
          const filteredCategories = categories.filter((cat) =>
            uniqueCategoryIds.includes(cat._id)
          );
          setCategories(filteredCategories);
          setFilters((prev) => ({ ...prev, category: "" }));
        } else {
          toast.error("Failed to fetch services for selected vendor");
          setServices([]);
        }
      } catch (err) {
        console.error("Error fetching services by vendor:", err);
        toast.error("Failed to load services: " + err.message);
        setServices([]);
      }
    };

    if (filters.vendor) {
      fetchServicesByVendor();
    } else {
      // Reset to all services if no vendor is selected
      const fetchAllServices = async () => {
        try {
          const token = localStorage.getItem("token");
          const serviceRes = await fetch(
            "https://visit.visitguru.inhttps://visitguru.in/api/v1/service/getService",
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
          const serviceData = await serviceRes.json();
          if (serviceData.success) {
            setServices(serviceData.data || []);
          }
        } catch (err) {
          console.error("Error fetching all services:", err);
          toast.error("Failed to load services: " + err.message);
        }
      };
      fetchAllServices();
    }
  }, [filters.vendor]);

  const handleDelete = async (serviceId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#11ADA3",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          "https://visit.visitguru.inhttps://visitguru.in/api/v1/service/deleteService",
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ serviceId }),
          }
        );

        const data = await response.json();

        if (data.success) {
          toast.success("Service deleted successfully!");
          setServices((prev) =>
            prev.filter((service) => service._id !== serviceId)
          );
          setSelectedIds((prev) => prev.filter((id) => id !== serviceId));
        } else {
          toast.error(data.message || "Failed to delete the service.");
        }
      } catch (error) {
        console.error("Error deleting service:", error);
        toast.error("Something went wrong!");
      }
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      toast.error("Please select at least one service to delete.");
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You are about to delete ${selectedIds.length} service(s). This action cannot be undone!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#11ADA3",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete them!",
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");

        const deletePromises = selectedIds.map((serviceId) =>
          fetch("https://visit.visitguru.inhttps://visitguru.in/api/v1/service/deleteService", {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ serviceId }),
          }).then((response) => response.json())
        );

        const results = await Promise.all(deletePromises);

        let successCount = 0;
        let errorMessages = [];

        results.forEach((data, index) => {
          if (data.success) {
            successCount++;
            setServices((prev) =>
              prev.filter((service) => service._id !== selectedIds[index])
            );
          } else {
            errorMessages.push(
              data.message || `Failed to delete service ${selectedIds[index]}.`
            );
          }
        });

        setSelectedIds([]);
        setBulkSelectName("");

        if (successCount === selectedIds.length) {
          toast.success(`${successCount} service(s) deleted successfully!`);
        } else if (successCount > 0) {
          toast.success(`${successCount} service(s) deleted successfully!`);
          if (errorMessages.length > 0) {
            toast.error(
              `Some services could not be deleted: ${errorMessages.join(", ")}`
            );
          }
        } else {
          toast.error(
            `Failed to delete services: ${errorMessages.join(", ")}`
          );
        }
      } catch (error) {
        console.error("Error deleting selected services:", error);
        toast.error("Something went wrong while deleting services!");
      }
    }
  };

  const handleUpdateSelected = async () => {
    if (!selectedCategory) {
      toast.error("Please select a category first.");
      return;
    }
    if (!newName && !newPrice) {
      toast.error("Please provide at least one update (name or price).");
      return;
    }

    // Filter services by selected category
    const categoryServices = services.filter(s =>
      (s.category?._id || s.category) === selectedCategory
    );

    if (categoryServices.length === 0) {
      toast.error("No services found for the selected category.");
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You are about to update ${categoryServices.length} service(s) in the selected category.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#11ADA3",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, update them!",
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem("token");
        const serviceIds = categoryServices.map(s => s._id);

        const res = await fetch("https://visit.visitguru.inhttps://visitguru.in/api/v1/service/updateMultipleServices", {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            serviceIds,
            serviceName: newName || undefined,
            price: newPrice ? parseFloat(newPrice) : undefined,
          }),
        });

        const data = await res.json();
        if (data.success) {
          toast.success(data.message);
          // Update local state
          setServices((prev) =>
            prev.map((s) =>
              serviceIds.includes(s._id)
                ? { ...s, serviceName: newName || s.serviceName, price: newPrice ? parseFloat(newPrice) : s.price }
                : s
            )
          );
          setSelectedCategory("");
          setNewName("");
          setNewPrice("");
        } else {
          toast.error(data.message);
        }
      } catch (err) {
        toast.error("Failed to update: " + err.message);
      }
    }
  };

  const getCategoryName = (categoryIdOrObj) => {
    const id =
      typeof categoryIdOrObj === "object"
        ? categoryIdOrObj?._id
        : categoryIdOrObj;
    const category = categories.find((cat) => cat._id === id);
    return category ? category.categoryName : "Unknown";
  };

  const getShopName = (shopId) => {
    if (!shopId) return "N/A";
    return (
      shopId.shopName ||
      `${shopId.firstName || ""} ${shopId.lastName || ""}`.trim() ||
      "N/A"
    );
  };

  const handleShopNameClick = (vendorId) => {
    const selectedVendor = vendors.find((v) => v._id === vendorId);
    if (selectedVendor) {
      setFilters((prev) => ({
        ...prev,
        vendor: vendorId,
        addedBy: "vendor",
      }));
      setSearchVendor(selectedVendor.shopName || "");
      setIsVendorOpen(false);
    }
  };

  const handleCategoryClick = (categoryId) => {
    const id = typeof categoryId === "object" ? categoryId?._id : categoryId;
    setFilters((prev) => ({ ...prev, category: id }));
    setPage(1);
  };

  const handleBulkSelect = (e) => {
    const name = e.target.value;
    setBulkSelectName(name);
    if (name) {
      let filteredServices = services.filter((s) => s.serviceName === name);

      // If a category is selected in filters, only show services from that category
      if (filters.category) {
        filteredServices = filteredServices.filter(s =>
          (s.category?._id || s.category) === filters.category
        );
      }

      const ids = filteredServices.map((s) => s._id);
      setSelectedIds(ids);
      setPage(1);
    } else {
      setSelectedIds([]);
    }
  };

  const filteredServices = services
    // If bulk selection is active, show only services matching that serviceName
    .filter((service) => (bulkSelectName ? service.serviceName === bulkSelectName : true))
    .filter((service) =>
      (service?.serviceName || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    )
    .filter((service) => {
      if (!filters.category) return true;
      const categoryId =
        typeof service.category === "object"
          ? service.category?._id
          : service.category;
      return categoryId === filters.category;
    })
    .filter((service) =>
      filters.addedBy
        ? service.addedBy?.toLowerCase() === filters.addedBy.toLowerCase()
        : true
    )
    .filter((service) => {
      if (filters.vendor) {
        const vendor = vendors.find((v) => v._id === filters.vendor);
        return (
          vendor &&
          service.shopId &&
          service.shopId.shopName === vendor.shopName
        );
      }
      return true;
    });

  const currentServices = filteredServices;

  const handlePrev = () => {
    if (page > 1) {
      setLoading(true);
      setTimeout(() => {
        setPage(page - 1);
        setLoading(false);
      }, 3000);
    }
  };

  const handleNext = () => {
    if (page < totalPages) {
      setLoading(true);
      setTimeout(() => {
        setPage(page + 1);
        setLoading(false);
      }, 3000);
    }
  };

  const handleSelectService = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(currentServices.map((service) => service._id));
    } else {
      setSelectedIds([]);
    }
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text("Services List", 14, 22);

    const tableColumn = [
      "#",
      "Service Name",
      "Price (₹)",
      "Category Name",
      "Shop Name",
      "Added By",
      "Selected",
    ];
    const tableRows = filteredServices.map((service, index) => [
      index + 1,
      service.serviceName || "N/A",
      `${service.price || 0}₹`,
      getCategoryName(service.category),
      getShopName(service.shopId),
      service.addedBy || "N/A",
      selectedIds.includes(service._id) ? "Yes" : "No",
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 30,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [8, 132, 196] },
    });

    doc.save("services-list.pdf");
  };

  useEffect(() => {
    setPage(1);
  }, [searchTerm, filters, bulkSelectName]);

  const vendorOptions = vendors.map((vendor) => ({
    value: vendor._id,
    label:
      vendor.shopName ||
      `${vendor.firstName || ""} ${vendor.lastName || ""}`.trim() ||
      "N/A",
  }));

  const filteredVendors = vendorOptions.filter((vendor) =>
    vendor.label.toLowerCase().includes(searchVendor.toLowerCase())
  );

  const addedByOptions = [
    { value: "", label: "All" },
    { value: "admin", label: "Admin" },
    { value: "vendor", label: "Vendor" },
  ];

  return (
    <div className="space-y-12">
      <div className="relative flex flex-col min-w-0 break-words bg-white w-full shadow-lg rounded">
        <div className="rounded-t px-4 py-3 border-0">
          <h3 className="font-semibold text-base text-blueGray-700 mb-2 flex justify-between items-center">
            <span>
              Services{" "}
              <span className="ml-2 text-sm text-blueGray">
                (Total Services: {totalServices}, Selected: {selectedIds.length})
              </span>
            </span>
            <div className="flex space-x-4">
              <button
                onClick={generatePDF}
                className="bg-[#0884c4] text-white px-4 py-2 rounded hover:bg-blue-600"
                style={{ backgroundColor: "#0884c4" }}
              >
                Download
              </button>
              <button
                onClick={handleDeleteSelected}
                className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
              >
                Delete
              </button>
              <button

                onClick={handleUpdateSelected}
                className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                style={{ backgroundColor: "#ff4500 " }}
              >
                Submit Update
              </button>
            </div>
          </h3>

          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div className="w-full md:w-1/3">
              <input
                type="text"
                placeholder="Search Services…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-blueGray-200 rounded focus:outline-none focus:ring text-sm"
              />
            </div>
            <div className="relative w-full md:w-1/4" ref={addedByDropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Added By
              </label>
              <div
                className="px-4 py-1.5 border border-blueGray-200 cursor-pointer rounded text-sm"
                onClick={() => {
                  setIsAddedByOpen((prev) => !prev);
                  setIsVendorOpen(false);
                }}
              >
                {addedByOptions.find((opt) => opt.value === filters.addedBy)
                  ?.label || "Select Added By"}
              </div>
              {isAddedByOpen && (
                <div className="absolute z-10 bg-white border border-blueGray-200 mt-1 w-full rounded shadow-lg max-h-60 overflow-y-auto">
                  {addedByOptions.map((option) => (
                    <div
                      key={option.value}
                      className="px-3 py-2 hover:bg-blue-100 cursor-pointer text-sm"
                      onClick={() => {
                        setFilters((prev) => ({
                          ...prev,
                          addedBy: option.value,
                          vendor: "",
                        }));
                        setIsAddedByOpen(false);
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {filters.addedBy === "vendor" && (
              <div className="relative w-full md:w-1/4" ref={vendorDropdownRef}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vendor
                </label>
                <div
                  className="px-4 py-1.5 border border-blueGray-200 cursor-pointer rounded text-sm"
                  onClick={() => {
                    setIsVendorOpen((prev) => !prev);
                    setIsAddedByOpen(false);
                  }}
                >
                  {vendorOptions.find((v) => v.value === filters.vendor)
                    ?.label || "Select Vendor"}
                </div>
                {isVendorOpen && (
                  <div className="absolute z-10 bg-white border border-blueGray-200 mt-1 w-full rounded shadow-lg max-h-60 overflow-y-auto">
                    <input
                      type="text"
                      placeholder="Search vendor..."
                      value={searchVendor}
                      onChange={(e) => setSearchVendor(e.target.value)}
                      className="w-full px-3 py-2 border-b border-blueGray-200 focus:outline-none text-sm"
                    />
                    <div
                      className="px-3 py-2 hover:bg-blue-100 cursor-pointer text-sm font-semibold"
                      onClick={() => {
                        setFilters((prev) => ({ ...prev, vendor: "" }));
                        setSearchVendor("");
                        setIsVendorOpen(false);
                      }}
                    >
                      All Vendors
                    </div>
                    {filteredVendors.length > 0 ? (
                      filteredVendors.map((vendor) => (
                        <div
                          key={vendor.value}
                          className="px-3 py-2 hover:bg-blue-100 cursor-pointer text-sm"
                          onClick={() => {
                            const selectedVendor = vendors.find(
                              (v) => v._id === vendor.value
                            );
                            setFilters((prev) => ({
                              ...prev,
                              vendor: vendor.value,
                              addedBy: "vendor",
                            }));
                            setSearchVendor(selectedVendor.shopName || "");
                            setIsVendorOpen(false);
                          }}
                        >
                          {vendor.label}
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-gray-500 text-sm">
                        No vendors found
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="w-full md:w-1/4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={filters.category}
                onChange={(e) => handleCategoryClick(e.target.value)}
                className="w-full px-3 py-1.5 border border-blueGray-200 rounded focus:outline-none focus:ring text-sm"
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat._id} value={cat._id}>
                    {cat.categoryName || "N/A"}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-full md:w-1/4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bulk Select Service
              </label>
              <select
                value={bulkSelectName}
                onChange={handleBulkSelect}
                className="w-full px-3 py-1.5 border border-blueGray-200 rounded focus:outline-none focus:ring text-sm"
              >
                <option value="">Select Service</option>
                {uniqueServiceNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-4 mt-4">
            <span className="text-sm font-medium text-gray-700">Bulk Update by Category:</span>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 border border-blueGray-200 rounded focus:outline-none focus:ring text-sm"
            >
              <option value="">Select Category</option>
              {categories.map((cat) => (
                <option key={cat._id} value={cat._id}>
                  {cat.categoryName || "N/A"}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="New Name (leave blank to keep)"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="px-3 py-2 border border-blueGray-200 rounded focus:outline-none focus:ring text-sm"
            />
            <input
              type="number"
              placeholder="New Price (leave blank to keep)"
              value={newPrice}
              onChange={(e) => setNewPrice(e.target.value)}
              className="px-3 py-2 border border-blueGray-200 rounded focus:outline-none focus:ring text-sm"
            />
          </div>
        </div>

        <div className="block w-full overflow-x-auto">
          <table className="items-center w-full bg-transparent border-collapse">
            <thead>
              <tr>
                {[
                  "#",
                  "Service Name",
                  "Price",
                  "Category Name",
                  "Shop Name",
                  "Added By",
                  "Action",
                  "",
                ].map((heading, idx) => (
                  <th
                    key={idx}
                    className="px-6 bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100 py-3 text-xs uppercase font-semibold text-left"
                  >
                    {heading === "" ? (
                      <input
                        type="checkbox"
                        style={{
                          width: "18px",
                          height: "18px",
                          accentColor: "#11ADA3",
                          border: "2px solid #ccc",
                          borderRadius: "6px",
                          boxShadow:
                            "0 2px 4px rgba(0,0,0,0.3), inset 0 -2px 4px rgba(0,0,0,0.2)",
                          transform: "scale(1)",
                          transition: "all 0.2s ease-in-out",
                          cursor: "pointer",
                        }}
                        checked={
                          currentServices.length > 0 &&
                          currentServices.every((service) =>
                            selectedIds.includes(service._id)
                          )
                        }
                        onChange={handleSelectAll}
                        onMouseOver={(e) =>
                          (e.target.style.transform = "scale(1.1)")
                        }
                        onMouseOut={(e) =>
                          (e.target.style.transform = "scale(1)")
                        }
                      />
                    ) : (
                      heading
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="p-8 text-center text-lg text-blueGray-600">
                    Loading...
                  </td>
                </tr>
              ) : (
                currentServices.map((service, index) => (
                  <tr key={service._id}>
                    <td className="border-t-0 px-6 text-xs p-4 text-left">
                      {(page - 1) * limit + index + 1}
                    </td>
                    <td className="border-t-0 px-6 text-xs p-4">
                      {service.serviceName || "N/A"}
                    </td>
                    <td className="border-t-0 px-6 text-xs p-4">
                      ₹{service.price || 0}
                    </td>
                    <td className="border-t-0 px-6 text-xs p-4">
                      <button
                        onClick={() => handleCategoryClick(service.category)}
                        className="text-blue-600 hover:underline focus:outline-none"
                        title="Filter by this category"
                      >
                        {getCategoryName(service.category)}
                      </button>
                    </td>
                    <td className="border-t-0 px-6 text-xs p-4">
                      {service.vendorID && service.shopId ? (
                        <button
                          onClick={() => handleShopNameClick(service.vendorID)}
                          className="text-blue-600 hover:underline focus:outline-none"
                          title="Filter by this vendor"
                        >
                          {getShopName(service.shopId)}
                        </button>
                      ) : (
                        getShopName(service.shopId)
                      )}
                    </td>
                    <td className="border-t-0 px-6 text-xs p-4">
                      {service.addedBy || "N/A"}
                    </td>
                    <td className="border-t-0 px-6 text-xs p-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() =>
                            navigate(`/admin/editservice/${service._id}`, {
                              state: service,
                            })
                          }
                          className="p-2 rounded-full hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-200 transition"
                          aria-label="Edit service"
                        >
                          <i className="fas fa-edit text-green-500 text-lg"></i>
                        </button>
                      </div>
                    </td>
                    <td className="border-t-0 px-6 text-xs p-4">
                      <input
                        type="checkbox"
                        style={{
                          width: "18px",
                          height: "18px",
                          accentColor: "#11ADA3",
                          border: "2px solid #ccc",
                          borderRadius: "6px",
                          boxShadow:
                            "0 2px 4px rgba(0,0,0,0.3), inset 0 -2px 4px rgba(0,0,0,0.2)",
                          transform: "scale(1)",
                          transition: "all 0.2s ease-in-out",
                          cursor: "pointer",
                        }}
                        checked={selectedIds.includes(service._id)}
                        onChange={() => handleSelectService(service._id)}
                        onMouseOver={(e) => (e.target.style.transform = "scale(1.1)")}
                        onMouseOut={(e) => (e.target.style.transform = "scale(1)")}
                      />
                    </td>
                  </tr>
                ))
              )}
              {!loading && currentServices.length === 0 && (
                <tr>
                  <td
                    colSpan="8"
                    className="text-center p-4 text-sm text-gray-500"
                  >
                    No services found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-between items-center p-4">
          <button
            onClick={handlePrev}
            disabled={page === 1 || loading}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm">
            Page {page} of {totalPages} ({totalServices} total services)
          </span>
          <button
            onClick={handleNext}
            disabled={page === totalPages || loading}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default GetService;
